'use client';

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Cluster, ClusterStats, MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { ActionIcon, Group, Menu, Text, useMantineTheme } from '@mantine/core';
import { useColorScheme } from "@mantine/hooks";
import { Sheet, SheetRef } from 'react-modal-sheet';
import { IconCheck, IconMap, IconMenu2, IconStack2 } from '@tabler/icons-react';
import { MdGpsFixed, MdRefresh, MdZoomInMap } from 'react-icons/md';
import { useRouter } from 'next/navigation';
import { Capacitor } from '@capacitor/core';
import { Position } from '@capacitor/geolocation';
import { notifications } from '@mantine/notifications';
import { StatusBar, Style } from '@capacitor/status-bar';
import { FaAngleDoubleDown, FaAngleDoubleUp } from 'react-icons/fa';
import {
    GoogleImageGallery,
    PlaceImageGallery,
    usePlaceImages,
} from '../SavedPlacesAppShell/PanelImageGallery';
import {
    AppShellType,
    MappableRecord,
    Place,
    PlaceClassType,
    PlaceRecord,
    PlaceType,
    SavedType,
} from '../../lib/interfaces';
import { getLogger } from '../../lib/logger';
import { googleMapsLoaderOptions, googleMapsMapId } from '../../lib/config';
import { Bounds, positionFromGoogleLatLng, positionFromLatLng, PositionKey, positionToKey } from '../../lib/position';
import { isOwnedBy, mapIconUrlForPlace, navigateToPlace, placeTypeFromPlace } from '../../lib/placeFunctions';
import { showAddEditTripSavedPlace } from '../AddEditPlace/addeditplace';
import { SheetsContext, SheetsData, sheetsMountPointId } from '../Sheets';
import { HomeContext, TripContext, UserContext } from '../../lib/context';
import { SavedPlaceBottom } from '../SavedPlaceScreen/SavedPlaceScreen';
import { useGeolocationData } from '../../lib/geolocation';
import { TSLoader } from '../TSLoader';
import { ActivityDetailsBottom } from '../TripActivityScreen/TripActivityScreen';
import { URLs } from '../PlaceSVGs';
import { showModalAsync } from "../TSModal";
import {
    layersBelowMinZoom,
    MAP_LAYER_LABELS,
    MapLayerId,
    MapLayers,
    TripInView,
} from '../../lib/mapLayers';

import classes from './GoogleMapPlaces.module.css';
import sheetClasses from '../TSSheet/TSSheet.module.css';
import { useUserById } from '../../lib/hooks';
import { hapticLightVibration } from '../../lib/haptics';

const logger = getLogger('GoogleMapPlaces');
const snapPoints = [ // +ve integers: px from bottom; -ve integers: px from top; fractions: percentages
    0.9,     // most of the way open
    0.4,     // a bit less than halfway open
    120,     // just the top of the sheet, including the handle
];

// All three of these are about the same on a mobile device
const TIGHT_ZOOM = 15;
const MIN_BOUNDS_WIDTH = 0.01;
const MIN_BOUNDS_HEIGHT = 0.01;
const MINIMUM_CLUSTER_SIZE = 5;
// How far beyond the viewport to build markers, as a fraction of the viewport's own span. Enough
// that a normal pan reveals markers that already exist, without paying for the whole globe.
const MARKER_VIEWPORT_PADDING = 0.5;

// A Transit place with both endpoints set. These are drawn as a dotted great-circle line rather than
// a pin, and excluded from the framing calculation so a long leg doesn't zoom the whole map out.
function transitRoute(record: MappableRecord): { from: google.maps.LatLngLiteral, to: google.maps.LatLngLiteral } | null {
    if (record.type !== 'place' || placeTypeFromPlace(record.place) !== PlaceType.Transit) {
        return null;
    }
    const { lat, lng, endlat, endlng } = record.place;
    if (lat == null || lng == null || endlat == null || endlng == null) {
        return null;
    }
    return { from: { lat, lng }, to: { lat: endlat, lng: endlng } };
}

// TODO
//   - propagate the full query in the refresh action
//   - when centering the map, take the sheet height into account
//   - put the zoom level and center into the url
type GoogleMapPlacesProps = {
    /**
     * All the place records to display on the map. This might be a mix of search
     * results, saved places and trip activities. Only those with positions will
     * be displayed.
     */
    recordsToMap: MappableRecord[],

    isMapVisible: boolean,
    selected: MappableRecord | undefined,
    onSelectionChanged: (record: MappableRecord | undefined) => unknown,
    appShellType: AppShellType,
    refreshRoute: string,

    /**
     * A unique identifier for a given mapping session. This might be a search result ID,
     * or a random number assigned when the map is first loaded. Automatic bounds recomputation
     * happens when the session ID changes, except when changing to a non-'undefined' value.
     */
    sessionId: any | undefined,

    setCurrentViewport?: (bounds: Bounds | undefined, zoomLevel: number | undefined) => unknown,
    toggleTravelScrollDrawer: () => unknown,
    currentUserId: string | undefined,
    activeTripOrGuideId: string | undefined,
    mapMountPointId?: string,
    // Whether to inset the selection sheet to the centred column on wide screens
    // (default true). See SearchableMap for why the guide map opts out.
    insetSheetOnWide?: boolean,

    // Optional overlays (prior visits, friends' favorites). The control is hidden when there are no
    // available layers — a map with no extra sources to offer shouldn't show an empty menu.
    layers?: MapLayers,
    availableLayers?: MapLayerId[],
    onToggleLayer?: (layer: MapLayerId) => unknown,
    // Trips with activities inside the current viewport, listed individually in the layers menu.
    tripsInViewport?: TripInView[],
    hiddenTrips?: Set<string>,
    onToggleTrip?: (tripId: string) => unknown,
    // Current zoom, so the layers menu can explain why an enabled layer isn't drawing anything.
    zoomLevel?: number,
};

function SheetComponent({ record, showInMap, onClick }: {
    record: MappableRecord,
    showInMap: (record: MappableRecord) => unknown
    onClick: (record: MappableRecord) => unknown
}) {
    const router = useRouter();
    const { tripAdjacentUsersById } = useContext(HomeContext);
    const sheets = useContext(SheetsContext);
    const { currentTrip } = useContext(TripContext);
    const userData = useContext(UserContext);
    const user = useUserById(userData.firebaseUserId);
    const placeImages = record.type === 'place'
        ? usePlaceImages({ docid: record.place.tripdocid ?? undefined }, record.place)
        : [];

    const [ address, setAddress ] = useState<string | undefined>();

    useEffect(() => {
        if (record.address) {
            record.address().then(setAddress);
        }
    }, [ record ]);

    const titleAndLocation = record.title && record.title !== ''
        && record.location && record.location !== '' && record.title !== record.location
        ? `${record.title} - ${record.location}`
        : record.title
            ? record.title
            : record.location;
    const tripUsers = [ ...tripAdjacentUsersById.values(), ...(user ? [ user ] : []) ] ?? [];
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
    return <div role='listitem' onClick={() => onClick(record)}>
        {record.type === 'place' && <PlaceImageGallery
            place={record.place}
            panel
            placeImages={placeImages}
            tripUsers={tripUsers}
        />}
        { record.type === 'search-engine-result' && record.searchEngineResult.type === 'google-search' &&
            <GoogleImageGallery placeId={record.searchEngineResult.value.place_id} /> }
        { titleAndLocation &&
            <Text
                aria-label='Place Name'
                style={{ textAlign: 'center' }}
                pt="xs"
                px="md"
                fw={500}
            >
                {titleAndLocation}
            </Text>
        }
        <Text
            aria-label='Address'
            style={{ textAlign: 'center', textOverflow: 'ellipsis', overflow: 'hidden' }}
            px='md'
            lineClamp={1}
            color='dimmed'
            size="sm">
            {address}
        </Text>
        <Group px="md" py="xs" justify='space-between'>
            { record.type === 'place' && record.place.placeclass === PlaceClassType.Saved &&
                <Text
                    size='sm'
                    onClick={() => navigateToPlace(router, (record as PlaceRecord).place)}
                >
                    Show Saved Place
                </Text>
            }
            { record.type === 'place' && record.place.placeclass === PlaceClassType.Activity &&
                <Text
                    size='sm'
                    onClick={() => navigateToPlace(router, (record as PlaceRecord).place)}
                >
                    Show in Trip
                </Text>
            }
            <Text size='sm' onClick={() => showInMap(record)}>
                Open in Maps
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                { record.type === 'place' && record.place.placeclass === PlaceClassType.Activity
                    && <ActivityDetailsBottom place={(record as PlaceRecord).place} /> }
                { record.type === 'place' && record.place.placeclass === PlaceClassType.Saved
                    && <SavedPlaceBottom place={record.place} showMapIcon={false} /> }
                { (record.type === 'search-engine-result') && userData.isLoggedIn
                    && <Text
                        size='sm'
                        onClick={async () => {
                            const result = record.searchEngineResult;
                            // TODO extract the activity type and propagate it somehow: extractPlaceTypeFromGoogleSuggestion(result.value)
                            //   This will take a bit of care since the data is stored in different fields for saved vs trip activities
                            showAddEditTripSavedPlace(
                                sheets,
                                {
                                    title: record.title,
                                    activityType: record.searchEngineResult.placeType,
                                    location: record.location,
                                    address: await record.address(),
                                    locality: await record.locality(),
                                    placeid: result.type === 'google-search' ? result.value.place_id : undefined,
                                    lat: record.position.lat,
                                    lng: record.position.lng,
                                },
                                currentTrip
                            );
                        }}>
                        Save to Travel Scroll
                    </Text>
                }
            </div>
        </Group>
    </div>;
}

export function GoogleMapPlaces(props: GoogleMapPlacesProps) {
    const homeData = useContext(HomeContext);

    if (homeData.userTrips || props.isMapVisible) {
        // If we haven't gotten fully connected yet (per userTrips) and we're rendering
        // the map in the background, don't bother loading. This reduces initial network load,
        // since Google Maps does a network fetch.
        return <GoogleMapPlacesInternal {...props} />;
    } else {
        return <>
            <div>
                <TSLoader id='GoogleMapPlaces' layout='fill-content-area' />
            </div>
        </>;
    }
}

function GoogleMapPlacesInternal(
    {
        recordsToMap,
        isMapVisible,
        selected,
        onSelectionChanged,
        appShellType,
        refreshRoute,
        sessionId: sessionIdParam,
        setCurrentViewport,
        toggleTravelScrollDrawer,
        currentUserId,
        activeTripOrGuideId,
        mapMountPointId,
        insetSheetOnWide,
        layers,
        availableLayers,
        onToggleLayer,
        tripsInViewport,
        hiddenTrips,
        onToggleTrip,
        zoomLevel,
    }: GoogleMapPlacesProps)
    : JSX.Element {
    // TODO if this hasn't been loaded, we should periodically try again when we go online,
    //  perhaps using the 'nonce' option.
    const { isLoaded } = useJsApiLoader(googleMapsLoaderOptions);

    const theme = useMantineTheme();
    const colorScheme = useColorScheme();
    const [ mapRef, setMapRef ] = useState<google.maps.Map>();
    const [ mapMode, setMapMode ] = useState<'roadmap' | 'hybrid'>('roadmap'); // TODO remember user preference
    const [mountEl, setMountEl] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setMountEl(document.getElementById(mapMountPointId ?? sheetsMountPointId));
    }, [mapMountPointId]);
    const { getPosition } = useGeolocationData(false); // We never track here -- we only use position on-demand within this component
    const [ renderState, setRenderState ] = useState<'unrendered' | 'first-with-zero' | 'first-with-results' | 'steady'>('unrendered');
    const sheets = useContext(SheetsContext);
    const { currentTrip } = useContext(TripContext);

    // Since we perform side effects (re-fitting) based on state, we need to use a state-based sessionId as well.
    // Otherwise, we end up performing re-fitting operations with meta-stable values -- params from a render
    // invocation, and state from the previously-set values. By using state for both, we line up the lifecycles.
    const [ sessionId, setSessionId ] = useState(sessionIdParam);

    const [ center, setCenter ] = useState<google.maps.LatLngLiteral>({
        lat: !selected ? 40 : selected?.position?.lat ?? 0,
        lng: !selected ? -70 : selected?.position?.lng ?? 0,
    });

    const selectedSheetRef = useRef<SheetRef>();

    const router = useRouter();

    const zoomToAtLeast = (targetZoom: number) => {
        const currentZoom = mapRef?.getZoom();
        const rounded = Math.round(targetZoom);
        if (currentZoom === undefined || currentZoom < rounded) {
            logger.log(`zoomToAtLeast(${targetZoom}) (rounded to ${rounded}). Prev: ${currentZoom}`);
            mapRef?.setZoom(rounded);
        }
    };

    const zoomTo = (item: MappableRecord) => {
        zoomToAtLeast(TIGHT_ZOOM);
        mapRef?.setCenter(item.position);
        mapRef?.panBy(0, 50); // TODO compute this based on the map height and the sheet reveal position
    };

    const jumpToCoordinates = async (position: Position) => {
        const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
        };
        logger.log(`jump to coordinates: ${JSON.stringify(coords)}`);
        zoomToAtLeast(TIGHT_ZOOM);
        mapRef?.setCenter(coords);
    };

    useEffect(() => {
        if (Capacitor.isNativePlatform() && mapMode === 'roadmap' && isMapVisible) {
            const promise = StatusBar.setStyle({ style: Style.Light });
            return () => { promise.finally(() => StatusBar.setStyle({ style: Style.Dark })); };
        }

        return undefined;
    }, [ mapMode, isMapVisible ]);

    useEffect(() => {
        if (selected) {
            selectedSheetRef?.current?.snapTo(1);
            zoomTo(selected);
        }
    }, [ selected?.id ]);

    useEffect(() => {
        // See commentary on sessionId state declaration
        setSessionId(sessionIdParam);
    }, [ sessionIdParam ]);

    const [
        mappableRecordsByPosition,
        mappableRecordPositionKeys, // distinct and in-order wrt. 'recordsToMap'
    ] = useMemo(() => {
        if (!recordsToMap) {
            return [ new Map<PositionKey, [MappableRecord, ...MappableRecord[]]>(), [] as string[] ];
        }

        // create map with key of placeids and arrays of corresponded places
        const recordsByPosition = new Map<PositionKey, [MappableRecord, ...MappableRecord[]]>();
        const recordPositions: string[] = [];
        recordsToMap.forEach(record => {
            // Transit legs with both endpoints render as a route line, not a marker.
            if (transitRoute(record)) {
                return;
            }
            if (record.position) {
                const key = positionToKey(record.position);
                const recordsAtPosition = recordsByPosition.get(key);
                if (!recordsAtPosition) {
                    recordsByPosition.set(key, [ record ]);
                    recordPositions.push(key); // only add an id to the list when we encounter a new place
                } else {
                    recordsAtPosition.push(record);
                }
            }
        });

        // we reverse the position keys due to the way that the marker clusterer assigns z-indexes
        recordPositions.reverse();

        return [ recordsByPosition, recordPositions ];
    }, [ recordsToMap, zoomLevel ]);

    // Only build markers for what is on (or near) screen.
    //
    // Saved places are not viewport-filtered upstream, so the full record set is every favorite the
    // user owns, anywhere on earth. Measured on a real account: 642 positions built for 34 visible.
    // Framing still uses the unfiltered set below -- this narrows only what becomes a DOM marker.
    //
    // The box is padded so a normal pan doesn't pop markers in, and it is only recomputed once the
    // viewport actually leaves it, so dragging doesn't rebuild markers on every frame.
    const markerBoundsRef = useRef<google.maps.LatLngBounds | undefined>();
    const [ markerBounds, setMarkerBounds ] = useState<google.maps.LatLngBounds | undefined>();

    const refreshMarkerBounds = () => {
        const view = mapRef?.getBounds();
        if (!view) {
            return;
        }
        const current = markerBoundsRef.current;
        if (current && current.contains(view.getNorthEast()) && current.contains(view.getSouthWest())) {
            return; // still inside the padded box; nothing to rebuild
        }
        const ne = view.getNorthEast();
        const sw = view.getSouthWest();
        const latPad = Math.abs(ne.lat() - sw.lat()) * MARKER_VIEWPORT_PADDING;
        const lngPad = Math.abs(ne.lng() - sw.lng()) * MARKER_VIEWPORT_PADDING;
        const padded = new google.maps.LatLngBounds(
            { lat: Math.max(-90, sw.lat() - latPad), lng: sw.lng() - lngPad },
            { lat: Math.min(90, ne.lat() + latPad), lng: ne.lng() + lngPad },
        );
        markerBoundsRef.current = padded;
        setMarkerBounds(padded);
    };

    const [ visibleRecordsByPosition, visibleRecordPositionKeys ] = useMemo(() => {
        if (!markerBounds) {
            return [ mappableRecordsByPosition, mappableRecordPositionKeys ];
        }
        const filtered = new Map<PositionKey, [MappableRecord, ...MappableRecord[]]>();
        mappableRecordsByPosition.forEach((records, key) => {
            // Keep the selected record even when it is off-screen, so its pin and sheet stay in sync.
            const isSelected = records.some(record => record.id === selected?.id);
            if (isSelected || markerBounds.contains(records[0].position)) {
                filtered.set(key, records);
            }
        });
        return [ filtered, mappableRecordPositionKeys.filter(key => filtered.has(key)) ];
    }, [ mappableRecordsByPosition, mappableRecordPositionKeys, markerBounds, selected?.id ]);

    const recomputeBounds = (recordsToConsider: Map<string, [MappableRecord, ...MappableRecord[]]> | undefined) => {
        const bounds = new google.maps.LatLngBounds();
        let itemsFound = false;
        recordsToConsider?.forEach((item, key) => {
            const first = item[0];
            // Transit endpoints shouldn't drive framing (a long leg would zoom the whole map out).
            if (first.type === 'place' && placeTypeFromPlace(first.place) === PlaceType.Transit) {
                return;
            }
            // Nor should optional layers: switching on "prior visits" is a request for context around
            // where you already are, not a request to zoom out to cover every trip you've ever taken.
            if (first.isOverlay) {
                return;
            }
            // When rendering a trip or guide map, we don't want to use the non trip or guide stuff when computing bounds.
            if (appShellType === AppShellType.Trip
                && (first.type === 'place' && first.place.placeclass === PlaceClassType.Activity)) {
                itemsFound = true;
                bounds!.extend(first.position);
            } else if (appShellType === AppShellType.Home) {
                itemsFound = true;
                bounds!.extend(first.position);
            } else if (appShellType === AppShellType.Saveds) {
                itemsFound = true;
                bounds!.extend(first.position);
            } else if (appShellType === AppShellType.Guide
                && (first.type === 'place' && (first.place.placeclass === PlaceClassType.Saved || first.place.placeclass === PlaceClassType.Guide))) {
                itemsFound = true;
                bounds!.extend(first.position);
            }
        });

        if (!itemsFound) {
            bounds.extend({ lat: 90, lng: -90 });
            bounds.extend({ lat: -90, lng: 30 });
        }

        const width = bounds.getSouthWest().lng() - bounds.getNorthEast().lng();
        const height = bounds.getNorthEast().lat() - bounds.getSouthWest().lat();

        if (width < MIN_BOUNDS_WIDTH) {
            const boundsCenter = bounds.getCenter();
            bounds.extend({ lat: boundsCenter.lat() + MIN_BOUNDS_WIDTH / 2, lng: boundsCenter.lng() });
            bounds.extend({ lat: boundsCenter.lat() - MIN_BOUNDS_WIDTH / 2, lng: boundsCenter.lng() });
        }
        if (height < MIN_BOUNDS_HEIGHT) {
            const boundsCenter = bounds.getCenter();
            bounds.extend({ lat: boundsCenter.lat(), lng: boundsCenter.lng() + MIN_BOUNDS_HEIGHT / 2 });
            bounds.extend({ lat: boundsCenter.lat(), lng: boundsCenter.lng() - MIN_BOUNDS_HEIGHT / 2 });
        }

        try {
            mapRef?.fitBounds(bounds!);
        } catch (ex) {
            console.error(ex);
        }
    };

    const refresh = () => {
        onSelectionChanged(undefined);
        recomputeBounds(mappableRecordsByPosition);
        router.push(refreshRoute);
    };

    // Draw a dotted great-circle line for each Transit leg with both endpoints (rebuilt when the
    // record set or map changes). The dotted look is a repeated dash symbol over an invisible stroke.
    const transitLinesRef = useRef<google.maps.Polyline[]>([]);
    useEffect(() => {
        transitLinesRef.current.forEach(line => line.setMap(null));
        transitLinesRef.current = [];
        if (!mapRef || !recordsToMap) {
            return undefined;
        }
        recordsToMap.forEach(record => {
            const route = transitRoute(record);
            if (!route) {
                return;
            }
            transitLinesRef.current.push(new google.maps.Polyline({
                path: [ route.from, route.to ],
                geodesic: true,
                strokeOpacity: 0,
                icons: [ {
                    icon: {
                        path: 'M 0,-1 0,1',
                        strokeOpacity: 0.75,
                        strokeColor: '#1c7ed6',
                        strokeWeight: 2,
                        scale: 3,
                    },
                    offset: '0',
                    repeat: '14px',
                } ],
                map: mapRef,
            }));
        });
        return () => {
            transitLinesRef.current.forEach(line => line.setMap(null));
            transitLinesRef.current = [];
        };
    }, [ mapRef, recordsToMap ]);

    useEffect(() => {
        if (!mapRef) {
            return;
        }
        switch (renderState) {
            case 'steady':
                return;
            case 'unrendered':
                if (recordsToMap.length === 0) {
                    setRenderState('first-with-zero');
                } else {
                    setRenderState('first-with-results');
                }
                break;
            case 'first-with-zero':
                if (recordsToMap.length > 0) {
                    setRenderState('first-with-results');
                }
                break;
            case 'first-with-results':
                setRenderState('steady');
                break;
        }
    }, [ mapRef, recordsToMap ]);

    useEffect(() => {
        // When the map 'sessionId' changes, do a bounds recomputation. This allows us to avoid
        // re-fitting just due to changes in hits to be mapped.
        if (mapRef && (sessionId !== undefined || renderState !== 'steady')) {
            if (selected) {
                zoomTo(selected);
            } else {
                recomputeBounds(mappableRecordsByPosition);
            }
        }
    }, [ mapRef, sessionId, renderState ]);

    //show in Map - takes to external google maps or apple maps depending on platform
    const showInMap = (record: MappableRecord) => {
        switch (record.type) {
            case 'place':
                openNatively(sheets, record.place);
                break;
            case undefined:
                notifications.show({ message: 'No place is selected!!' });
                break;
            case 'search-engine-result':
                notifications.show({ message: 'TODO Opening search results is not yet implemented' });
                break;
        }
    };

    const selectionSheet = <Sheet
        aria-label='Map Selection Detail'
        style={{
            zIndex: 100, // In _app.tsx, we set the header to 99 and the footer to 101, so we can put this in between
        }}
        ref={selectedSheetRef}
        mountPoint={mountEl ?? undefined}
        isOpen={isMapVisible && !!selected}
        onClose={() => onSelectionChanged(undefined)}
        snapPoints={snapPoints}
        initialSnap={1}
    >
        <Sheet.Container className={(insetSheetOnWide ?? true) ? sheetClasses.wideContainer : undefined}>
            <Sheet.Header
                style={{
                    backgroundColor: colorScheme === 'dark'
                        ? theme.colors.dark[5]
                        : theme.colors.light as unknown as string }} />
            <Sheet.Content
                className='tscroll-footer'
                style={{
                    backgroundColor: colorScheme === 'dark'
                        ? theme.colors.dark[5]
                        : theme.colors.light as unknown as string,
                }}>
                <Sheet.Scroller>
                    { selected && <SheetComponent
                        key='selected-sheet-component'
                        record={selected}
                        showInMap={showInMap}
                        onClick={() => { }}
                    /> }
                </Sheet.Scroller>
            </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop style={{ backgroundColor: 'transparent' }} />
    </Sheet>;

    if (!isLoaded) {
        return <>
            {selectionSheet}
            <div>
                <TSLoader id='GoogleMapPlacesInternal' layout='fill-content-area' />
            </div>
        </>;
    }

    const jumpToCurrentPosition = async () => {
        const position = await getPosition();
        if (position) {
            // TODO detect if we've manually reframed in the meantime, and suppress the position jump if so
            await jumpToCoordinates(position);
        }
    };

    const toggleMapMode = () => {
        switch (mapMode) {
            case 'hybrid':
                setMapMode('roadmap');
                break;
            case 'roadmap':
                setMapMode('hybrid');
                break;
        }
    };

    return (
        <>
            {selectionSheet}
            {<div style={{ height: '100%', position: 'relative', zoom: '110%' }}>
                <GoogleMap
                    mapContainerStyle={{ height: '100%' }}
                    center={center}
                    onLoad={map => setMapRef(map)}
                    tilt={0} // prevent tilting even if 45° imagery is available
                    options={{
                        mapId: googleMapsMapId, // required for AdvancedMarkerElement
                        minZoom: 2,
                        mapTypeId: mapMode,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                        keyboardShortcuts: false,
                        zoomControl: false,
                        gestureHandling: 'greedy',
                        isFractionalZoomEnabled: false,
                        colorScheme: google.maps.ColorScheme.FOLLOW_SYSTEM,
                    }}
                    onBoundsChanged={() => {
                        refreshMarkerBounds();
                        if (setCurrentViewport) {
                            const googleBounds = mapRef?.getBounds();
                            const newCenter = googleBounds && validateLatLng('center', googleBounds.getCenter());
                            const sw = googleBounds && validateLatLng('sw', googleBounds.getSouthWest());
                            const ne = googleBounds && validateLatLng('ne', googleBounds.getNorthEast());
                            if (googleBounds && newCenter && sw && ne) {
                                const bounds = {
                                    center: positionFromGoogleLatLng(newCenter),
                                    southWest: positionFromGoogleLatLng(sw),
                                    northEast: positionFromGoogleLatLng(ne),
                                };
                                setCurrentViewport(bounds, mapRef?.getZoom());
                            } else {
                                setCurrentViewport(undefined, mapRef?.getZoom());
                            }
                        }
                    }}
                    onClick={(e: google.maps.MapMouseEvent | google.maps.IconMouseEvent) => {
                        onSelectionChanged(undefined);

                        if (selected) {
                            // When we click anywhere on a map (including on a POI) while a selection is active,
                            // simply dismiss the selection. This avoids opening POIs when the intention is probably
                            // just to dismiss the selection.
                            e.stop();
                            return;
                        }

                        const { placeId } = e as google.maps.IconMouseEvent;
                        if (!placeId) {
                            // We've clicked in non-POI map space -- close the existing POI window
                            closePOIWindow();
                            return;
                        }

                        // Anonymous (no currentUserId) viewers get a read-only map: don't offer "Add to Travel Scroll".
                        if (!currentUserId) {
                            return;
                        }

                        const position = positionFromLatLng({ lat: e.latLng?.lat(), lng: e.latLng?.lng() });
                        const editPoiWindow = () => {
                            const poiWindow = document.querySelector('.poi-info-window');
                            if (poiWindow) {
                                const link: HTMLAnchorElement | null = poiWindow.querySelector('.view-link a');
                                if (link) {
                                    link.href = '';
                                    link.text = "Add to Travel Scroll";
                                    link.onclick = async event => {
                                        event.preventDefault(); // prevent the actual link traversal from happening

                                        // It's important to fetch these values inside the click listener. In Safari,
                                        // the same strings captured at link edit time get cleared to the empty string
                                        // by the time the event handler click happens.
                                        const title = (poiWindow.querySelector('.title') as HTMLDivElement)?.innerText;
                                        const address = [ ...poiWindow.querySelectorAll('.address-line').values() ]
                                            .map(line => (line as HTMLDivElement).innerText)
                                            .join(', ');
                                        await showAddEditTripSavedPlace(
                                            sheets,
                                            {
                                                placeid: placeId,
                                                title,
                                                location: title,
                                                address,
                                                lat: position?.lat,
                                                lng: position?.lng,
                                            },
                                            currentTrip);
                                        closePOIWindow();
                                    };
                                }
                                return true;
                            } else {
                                return false;
                            }
                        };

                        // The div isn't added in time for the onClick event, but will be there after a render cycle.
                        if (!editPoiWindow()) {
                            setTimeout(editPoiWindow);
                        }
                    }}
                    onDblClick={() => {
                        const zoom = mapRef?.getZoom();
                        if (zoom) {
                            const scaled = Math.round(zoom + 1);
                            logger.log(`Double-click: setting zoom to ${scaled}`);
                            mapRef?.setZoom(scaled);
                        }
                    }}
                >
                    <>
                        {/* Pins + titles are AdvancedMarkerElements managed imperatively (the
                            @react-google-maps/api wrapper has no Advanced Marker component). Google
                            positions them atomically with the map and declutters overlapping titles
                            via native collisionBehavior — no OverlayView, so no scroll-stop flash. */}
                        {isMapVisible && <AdvancedPlaceMarkers
                            map={mapRef}
                            recordsByPosition={visibleRecordsByPosition}
                            positionKeys={visibleRecordPositionKeys}
                            selected={selected}
                            currentUserId={currentUserId}
                            activeTripId={activeTripOrGuideId}
                            colorScheme={colorScheme}
                            onSelect={onSelectionChanged}
                        />}
                        <PositionMarker isMapVisible={isMapVisible} />
                    </>
                </GoogleMap>
                { isMapVisible &&
                    [
                        {
                            top: "calc(1rem + env(safe-area-inset-top))",
                            left: "1rem",
                            right: undefined,
                            icon: <IconMenu2 size={18} />,
                            onClick: () => toggleTravelScrollDrawer(),
                        },
                        {
                            top: "calc(4rem + env(safe-area-inset-top))",
                            left: "1rem",
                            right: undefined,
                            icon: <IconMap size={18} />,
                            onClick: toggleMapMode,
                        },
                        {
                            top: "calc(1rem + env(safe-area-inset-top))",
                            left: undefined,
                            right: "1rem",
                            icon: <MdRefresh size={18} />,
                            onClick: refresh,
                        },
                        {
                            top: "calc(4rem + env(safe-area-inset-top))",
                            left: undefined,
                            right: "1rem",
                            icon: <MdGpsFixed size={18} />,
                            onClick: jumpToCurrentPosition,
                        },
                    ].map((icon, index) => (
                        <ActionIcon
                            key={index}
                            className={mapMode === 'roadmap' || colorScheme === 'dark' ? classes.darkButton : classes.lightButton}
                            style={{
                                position: "absolute",
                                top: icon.top,
                                left: icon.left,
                                right: icon.right,
                            }}
                            size="lg"
                            radius="xl"
                            onClick={icon.onClick}
                        >
                            {icon.icon}
                        </ActionIcon>
                    ))
                }
                { isMapVisible && layers && onToggleLayer && onToggleTrip && !!availableLayers?.length && <MapLayersMenu
                    layers={layers}
                    availableLayers={availableLayers}
                    onToggle={onToggleLayer}
                    tripsInViewport={tripsInViewport ?? []}
                    hiddenTrips={hiddenTrips ?? new Set()}
                    onToggleTrip={onToggleTrip}
                    zoomLevel={zoomLevel}
                    buttonClassName={mapMode === 'roadmap' || colorScheme === 'dark' ? classes.darkButton : classes.lightButton}
                /> }
                { isMapVisible && <SlideZoomer
                    minZoom={2}
                    maxZoom={21}
                    mapRef={mapRef}
                    background={mapMode === 'roadmap' || colorScheme === 'dark' ? 'dark' : 'light'}
                /> }
            </div>}
        </>
    );
}

/**
 * The map's layers control: switches optional overlays (prior visits, friends' favorites) on and off.
 *
 * These sources were previously reachable only as search chips, so they'd appear as pins only if you
 * typed a query — there was no way to just browse where you'd been. Sits under the map-mode button in
 * the left-hand control stack.
 */
function MapLayersMenu({
    layers, availableLayers, onToggle, tripsInViewport, hiddenTrips, onToggleTrip, zoomLevel, buttonClassName,
}: {
    layers: MapLayers,
    availableLayers: MapLayerId[],
    onToggle: (layer: MapLayerId) => unknown,
    tripsInViewport: TripInView[],
    hiddenTrips: Set<string>,
    onToggleTrip: (tripId: string) => unknown,
    zoomLevel: number | undefined,
    buttonClassName: string,
}) {
    // Per-layer, not global: favorites draw at any zoom, trip activities need you zoomed in.
    const dormant = layersBelowMinZoom(layers, availableLayers, zoomLevel);

    return (
        // closeOnItemClick={false} keeps the menu open across several toggles: layers are something
        // you tune while watching the map, and reopening the menu per toggle is tedious.
        <Menu position="right-start" withinPortal shadow="md" width={260} closeOnItemClick={false}>
            <Menu.Target>
                <ActionIcon
                    className={buttonClassName}
                    style={{
                        position: 'absolute',
                        top: 'calc(7rem + env(safe-area-inset-top))',
                        left: '1rem',
                    }}
                    size="lg"
                    radius="xl"
                    aria-label="Map Layers"
                >
                    <IconStack2 size={18} />
                </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown mah="70vh" style={{ overflowY: 'auto' }}>
                <Menu.Label>Show on map</Menu.Label>
                {availableLayers.map(id => (
                    <Menu.Item
                        key={id}
                        onClick={() => onToggle(id)}
                        rightSection={layers[id] ? <IconCheck size={14} /> : null}
                    >
                        <Text size="sm">{MAP_LAYER_LABELS[id]}</Text>
                        {/* Only this layer is dormant — say so here rather than in a footer note,
                            so it's obvious which switch is currently inert. */}
                        {dormant.includes(id) && <Text size="xs" c="dimmed">Zoom in to show</Text>}
                    </Menu.Item>
                ))}

                {/* Named trips rather than an opaque "prior visits" switch: the whole point of
                    browsing the past on a map is knowing *which* trip a cluster of pins belongs to. */}
                {layers.tripActivities && tripsInViewport.length > 0 && <>
                    <Menu.Divider />
                    <Menu.Label>Trips in view</Menu.Label>
                    {tripsInViewport.map(trip => (
                        <Menu.Item
                            key={trip.tripId}
                            onClick={() => onToggleTrip(trip.tripId)}
                            rightSection={hiddenTrips.has(trip.tripId) ? null : <IconCheck size={14} />}
                        >
                            <Text size="sm" lineClamp={1}>{trip.title}</Text>
                            <Text size="xs" c="dimmed">
                                {trip.date
                                    ? trip.date.toLocalDateTime().toLocaleString({ month: 'short', year: 'numeric' })
                                    : 'No dates'}
                                {` · ${trip.activityCount} here`}
                            </Text>
                        </Menu.Item>
                    ))}
                </>}

            </Menu.Dropdown>
        </Menu>
    );
}

// The pin graphic for one AdvancedMarkerElement. Anchored bottom-centre on the
// coordinate, which puts a pin image's tip on the point; blue dots are shifted to
// sit centred on the point instead.
function buildPinContent(v: { url: string, width: number, height: number, isPin: boolean }): HTMLElement {
    const root = document.createElement('div');
    root.className = 'adv-marker';
    root.style.cursor = 'pointer';

    const img = document.createElement('img');
    img.src = v.url;
    img.width = v.width;
    img.height = v.height;
    img.draggable = false;
    img.alt = '';
    root.appendChild(img);

    if (!v.isPin) {
        root.style.transform = `translateY(${v.height / 2}px)`;
    }
    return root;
}

// The title for one AdvancedMarkerElement, a separate marker from the pin so it can
// be decluttered independently. Anchored bottom-centre on the coordinate, then dropped
// just below the pin tip.
function buildLabelContent(v: { title: string, isSelected: boolean, colorScheme: 'light' | 'dark' }): HTMLElement {
    const label = document.createElement('div');
    label.className = `map-place-label${v.isSelected ? ' map-place-label--selected' : ''}`;
    label.textContent = v.title;
    label.style.color = v.colorScheme === 'dark' ? '#f0f0f0' : '#1a1a1a';
    label.style.cursor = 'pointer';
    label.style.transform = 'translateY(calc(100% + 4px))';
    return label;
}

// Custom cluster bubble renderer. Mirrors @googlemaps/markerclusterer's DefaultRenderer
// (concentric translucent circles + the count), but recolored: blue for larger-than-average
// clusters and green for smaller ones, so the size cue is preserved.
const clusterRenderer = {
    render(cluster: Cluster, stats: ClusterStats): google.maps.marker.AdvancedMarkerElement {
        const { count, position } = cluster;
        const color = count > Math.max(10, stats.clusters.markers.mean) ? '#1a73e8' : '#34a853';
        const svg = `<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 240 240">
            <circle cx="120" cy="120" opacity=".6" r="70" />
            <circle cx="120" cy="120" opacity=".3" r="90" />
            <circle cx="120" cy="120" opacity=".2" r="110" />
            <circle cx="120" cy="120" opacity=".1" r="130" />
            <text x="50%" y="50%" style="fill:#fff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="-apple-system, system-ui, sans-serif">${count}</text>
        </svg>`;
        const svgEl = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement;
        svgEl.setAttribute('transform', 'translate(0 25)');
        return new google.maps.marker.AdvancedMarkerElement({
            position,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            content: svgEl,
        });
    },
};

// Renders the place/activity pins as AdvancedMarkerElements managed imperatively
// (the @react-google-maps/api wrapper has no Advanced Marker component) and clusters
// the pins with @googlemaps/markerclusterer. Each place is two markers: a REQUIRED pin
// (always visible) and an OPTIONAL_AND_HIDES_LOWER_PRIORITY title that Google declutters
// natively. Titles aren't clustered; they are shown only while their pin is rendered
// individually (and hidden when it's absorbed into a cluster). Nothing uses OverlayView,
// so there is no scroll-stop flash. Markers are diffed by position key so a plain pan
// touches nothing.
function AdvancedPlaceMarkers({
    map,
    recordsByPosition,
    positionKeys,
    selected,
    currentUserId,
    activeTripId,
    colorScheme,
    onSelect,
}: {
    map: google.maps.Map | undefined,
    recordsByPosition: Map<string, [MappableRecord, ...MappableRecord[]]>,
    positionKeys: string[],
    selected: MappableRecord | undefined,
    currentUserId: string | undefined,
    activeTripId: string | undefined,
    colorScheme: 'light' | 'dark',
    onSelect: (record: MappableRecord) => unknown,
}): null {
    const clustererRef = useRef<MarkerClusterer>();
    const syncLabelsRef = useRef<() => void>();
    const pinsRef = useRef<Map<string, {
        marker: google.maps.marker.AdvancedMarkerElement,
        sig: string,
        listener: google.maps.MapsEventListener,
    }>>(new Map());
    const labelsRef = useRef<Map<string, {
        marker: google.maps.marker.AdvancedMarkerElement,
        sig: string,
        listener: google.maps.MapsEventListener,
        visible: boolean,
    }>>(new Map());
    // Reachable from marker click listeners so we never rebuild listeners on data change.
    const recordsByPositionRef = useRef(recordsByPosition);
    recordsByPositionRef.current = recordsByPosition;
    const onSelectRef = useRef(onSelect);
    onSelectRef.current = onSelect;

    // Create the clusterer once the map is ready; tear it (and every marker) down on unmount.
    useEffect(() => {
        if (!map) {
            return undefined;
        }
        const clusterer = new MarkerClusterer({
            map,
            markers: [],
            algorithm: new SuperClusterAlgorithm({ radius: 60, minPoints: MINIMUM_CLUSTER_SIZE, maxZoom: TIGHT_ZOOM }),
            renderer: clusterRenderer,
        });
        clustererRef.current = clusterer;

        // Titles aren't clustered; a title is shown only while its pin is rendered on its own.
        // The clusterer sets a clustered pin's `.map` to null and an individually-rendered pin's
        // `.map` to the map, so we key off that supported signal rather than its internal cluster
        // list, and only touch a title whose attached/detached state actually flipped.
        const syncLabels = () => {
            labelsRef.current.forEach((entry, key) => {
                const pin = pinsRef.current.get(key);
                const visible = !!pin && pin.marker.map != null;
                if (entry.visible !== visible) {
                    entry.marker.map = visible ? map : null;
                    entry.visible = visible;
                }
            });
        };
        syncLabelsRef.current = syncLabels;
        const listener = clusterer.addListener('clusteringend', syncLabels);

        return () => {
            listener.remove();
            clusterer.clearMarkers();
            clusterer.setMap(null); // detaches the clusterer's own map 'idle' listener (clearMarkers doesn't)
            pinsRef.current.forEach((entry) => { entry.listener.remove(); });
            labelsRef.current.forEach((entry) => { entry.listener.remove(); entry.marker.map = null; });
            pinsRef.current.clear();
            labelsRef.current.clear();
            syncLabelsRef.current = undefined;
            clustererRef.current = undefined;
        };
    }, [ map ]);

    // Reconcile pins + titles with the current records and selection.
    useEffect(() => {
        const clusterer = clustererRef.current;
        if (!map || !clusterer) {
            return;
        }
        const pins = pinsRef.current;
        const labels = labelsRef.current;
        const addedPins: google.maps.marker.AdvancedMarkerElement[] = [];
        const removedPins: google.maps.marker.AdvancedMarkerElement[] = [];
        let labelsChanged = false;
        const seen = new Set<string>();

        positionKeys.forEach((key, index) => {
            const results = recordsByPosition.get(key);
            if (!results) {
                return;
            }
            const item = results[0];
            const icon = iconForItem(results, selected, currentUserId, activeTripId);
            if (!icon) {
                return;
            }
            seen.add(key);
            const isSelected = item.id === selected?.id;
            const isPin = item.type === 'place';
            const width = icon.scaledSize.width;
            const height = icon.scaledSize.height;
            // Later keys sit on top (higher z); selection always wins both draw order and collisions.
            const zIndex = isSelected ? 1_000_000 : index;
            const locationFallback = item.type === 'place' && item.location && item.location.length <= 50
                ? item.location : '';
            const title = item.type === 'place' ? (item.title || locationFallback) : '';

            const onClick = () => {
                const latest = recordsByPositionRef.current.get(key)?.[0];
                if (latest) {
                    onSelectRef.current(latest);
                }
            };

            // Pin marker — always shown (REQUIRED). gmpClickable makes Google treat it as
            // interactive and fire 'gmp-click' (and suppress the map's own click, which would
            // otherwise deselect), instead of the click leaking through to the map.
            const pinSig = `${icon.url}|${isPin ? 1 : 0}|${width}x${height}|${zIndex}`;
            const currentPin = pins.get(key);
            if (!currentPin) {
                const marker = new google.maps.marker.AdvancedMarkerElement({
                    position: item.position,
                    content: buildPinContent({ url: icon.url, width, height, isPin }),
                    zIndex,
                    gmpClickable: true,
                    collisionBehavior: google.maps.CollisionBehavior.REQUIRED,
                });
                const pinListener = marker.addListener('gmp-click', onClick);
                pins.set(key, { marker, sig: pinSig, listener: pinListener });
                addedPins.push(marker);
            } else if (currentPin.sig !== pinSig) {
                currentPin.marker.position = item.position;
                currentPin.marker.content = buildPinContent({ url: icon.url, width, height, isPin });
                currentPin.marker.zIndex = zIndex;
                currentPin.sig = pinSig;
            }

            // Title marker — decluttered natively (OPTIONAL). Only for titled place pins.
            const currentLabel = labels.get(key);
            if (isPin && title) {
                const labelSig = `${title}|${isSelected ? 1 : 0}|${colorScheme}|${zIndex}`;
                if (!currentLabel) {
                    const marker = new google.maps.marker.AdvancedMarkerElement({
                        position: item.position,
                        content: buildLabelContent({ title, isSelected, colorScheme }),
                        zIndex,
                        gmpClickable: true,
                        collisionBehavior: google.maps.CollisionBehavior.OPTIONAL_AND_HIDES_LOWER_PRIORITY,
                    });
                    const labelListener = marker.addListener('gmp-click', onClick);
                    // .map starts null (detached); syncLabels attaches it once its pin is placed.
                    labels.set(key, { marker, sig: labelSig, listener: labelListener, visible: false });
                    labelsChanged = true;
                } else if (currentLabel.sig !== labelSig) {
                    currentLabel.marker.position = item.position;
                    currentLabel.marker.content = buildLabelContent({ title, isSelected, colorScheme });
                    currentLabel.marker.zIndex = zIndex;
                    currentLabel.sig = labelSig;
                }
            } else if (currentLabel) {
                currentLabel.listener.remove();
                currentLabel.marker.map = null;
                labels.delete(key);
                labelsChanged = true;
            }
        });

        pins.forEach((entry, key) => {
            if (!seen.has(key)) {
                entry.listener.remove();
                removedPins.push(entry.marker);
                pins.delete(key);
            }
        });
        labels.forEach((entry, key) => {
            if (!seen.has(key)) {
                entry.listener.remove();
                entry.marker.map = null;
                labels.delete(key);
                labelsChanged = true;
            }
        });

        if (removedPins.length) {
            clusterer.removeMarkers(removedPins, true);
        }
        if (addedPins.length) {
            clusterer.addMarkers(addedPins, true);
        }
        if (addedPins.length || removedPins.length) {
            // Re-clustering fires clusteringend, which re-syncs every title's visibility.
            clusterer.render();
        } else if (labelsChanged) {
            // Titles changed but pin membership didn't, so the clusterer won't re-render on its
            // own — sync the new/removed titles against their (unchanged) pins directly.
            syncLabelsRef.current?.();
        }
    }, [ map, recordsByPosition, positionKeys, selected, currentUserId, activeTripId, colorScheme ]);

    return null;
}

function closePOIWindow() {
    const existingPoiWindow = document.querySelector('.poi-info-window');
    const dialog = existingPoiWindow?.parentElement?.closest('[role="dialog"]');
    dialog?.querySelector('button')?.click();
}

const smallSize = 40;
const largeSize = 80;
function iconForItem(
    results: [MappableRecord, ...MappableRecord[]],
    selected: { id: string } | undefined,
    currentUserId: string | undefined,
    activeTripId: string | undefined,
) {
    const item = results[0];
    const isSelected = item.id === selected?.id;
    const size = isSelected ? largeSize : smallSize;
    const scaledSize = new google.maps.Size(size, size);

    if (item.type === 'place') {
        // scan the other records to see if the place is a favorite or want-to-go of the current user
        // isOwnedBy, not a bare place.userid check: userid is the legacy owner field and is null on
        // anything written since creatorId replaced it, so comparing it directly reports "not yours"
        // for most current records — and the favorite/want-to-go pin variant would never be picked.
        const selfSavedCategories = Array.from(new Set(results
            .filter(record =>
                record.type === 'place'
                && isOwnedBy((record as PlaceRecord).place, currentUserId)
                && record.place.placeclass === PlaceClassType.Saved)
            .flatMap(record => (record as PlaceRecord).place.categories)
            .filter(savedType => !!savedType) as SavedType[]));
        return { url: mapIconUrlForPlace(item.place, selfSavedCategories, activeTripId), scaledSize };
    }
    return { url: URLs.ActiveTrip.TripActivity.BlueDot.map, scaledSize };
}

function isPositionValid(position: Position | undefined): position is Position {
    if (!position) {
        return false;
    } else if (position.coords?.latitude !== null && position.coords?.latitude !== undefined
            && position.coords?.longitude !== null && position.coords?.longitude !== undefined) {
        return true;
    } else {
        const msg = `Received a non-null userPosition that contains null coords!
         coords=${position.coords}; lat=${position.coords?.latitude}; lng=${position.coords?.longitude}`;
        notifications.show({
            title: `Google Maps precondition failure`,
            message: msg,
            autoClose: true,
        });
        logger.warn(msg);
        return false;
    }
}

function validateLatLng(context: string, latLng: google.maps.LatLng) {
    const isValid = !!latLng.lat && !!latLng.lng;
    if (isValid) {
        return latLng;
    } else {
        const msg = `Invalid latLng for '${context}'! lat: ${latLng.lat}; lng: ${latLng.lng}`;
        notifications.show({ autoClose: true, title: 'Google Maps precondition failure', message: msg });
        logger.warn(msg);
        return undefined;
    }
}

function SlideZoomer(props: {
    minZoom: number,
    maxZoom: number,
    mapRef: google.maps.Map | undefined,
    background: 'dark' | 'light',
}) {
    const [ dragStartData, setDragStartData ] = useState<{ y: number, zoom: number }>();
    const [ lastY, setLastY ] = useState<number>();

    useEffect(() => {
        if (dragStartData === undefined) {
            setLastY(undefined);
        }
    }, [ dragStartData ]);

    function handleMove(clientY: number) {
        setLastY(clientY);
        if (dragStartData !== undefined) {
            const offset = clientY - dragStartData.y;
            const newZoom = dragStartData.zoom + offset / 60;
            const targetZoom = Math.min(
                props.maxZoom,
                Math.max(
                    props.minZoom,
                    Math.round(newZoom)));
            if (targetZoom !== props.mapRef?.getZoom()) {
                logger.log(`Slide-zooming to ${targetZoom}`);
                props.mapRef?.setZoom(targetZoom);
                hapticLightVibration();
            }
        }
    }

    const delta = dragStartData === undefined || lastY === undefined ? 0 : lastY - dragStartData.y;
    return <div
        style={{
            position: 'absolute',
            top: `calc(15rem + env(safe-area-inset-top) + ${delta}px)`,
            right: '1rem',
            // we translate this so that both the button and the slider div end up
            // sharing the same centerpoint regardless of extra affordances
            transform: 'translate(0, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
        }}
        onMouseDown={event => {
            if (event.buttons === 1) {
                const zoom = props.mapRef?.getZoom();
                if (zoom) {
                    setLastY(event.clientY);
                    setDragStartData({ y: event.clientY, zoom });
                }
            } else {
                setDragStartData(undefined);
            }
        }}
        onMouseMove={event => {
            if (event.buttons === 1) {
                handleMove(event.clientY);
            }
        }}
        onMouseUp={event => {
            setDragStartData(undefined);
        }}
        onTouchStart={event => {
            const zoom = props.mapRef?.getZoom();
            if (zoom) {
                setLastY(event.touches[0].clientY);
                setDragStartData({ y: event.touches[0].clientY, zoom });
            }
        }}
        onTouchMove={event => {
            handleMove(event.touches[0].clientY);
        }}
        onTouchEnd={() => {
            setDragStartData(undefined);
        }}
    >
        <FaAngleDoubleUp
            style={{
                visibility: dragStartData === undefined ? 'hidden' : 'visible',
            }}
            color={props.background === 'dark' ? 'black' : 'white'}
        />
        <ActionIcon
            className={props.background === 'dark' ? classes.darkButton : classes.lightButton}
            variant={props.background === 'dark' ? "filled" : "light"}
            size="lg"
            radius="xl"
        >
            <MdZoomInMap size={18} />
        </ActionIcon>
        <FaAngleDoubleDown
            style={{
                visibility: dragStartData === undefined ? 'hidden' : 'visible',
            }}
            color={props.background === 'dark' ? 'black' : 'white'}
        />
    </div>;
}

async function openNatively(sheets: SheetsData, place: Place) {
    const processedAddress = place.address?.replaceAll('\n', ' ') ?? '';

    const googleParams = [ `q=${place.title} ${place.location} ${processedAddress}` ];
    if (place.placeid) {
        googleParams.unshift(`query_place_id=${place.placeid}`);
    }
    const googleMapsUrl = `https://maps.google.com/maps?${googleParams.join('&')}`;

    if ("platform" in navigator) {
        if ((navigator.platform.indexOf("iPhone") !== -1) ||
            (navigator.platform.indexOf("iPad") !== -1) ||
            (navigator.platform.indexOf("iPod") !== -1)) {
            const appleMapsParams = [ `q=${place.title}` ];
            if (place.address) {
                appleMapsParams.push(`address=${place.location}, ${place.address.replaceAll('\n', ',')}`);
            } else {
                const position = positionFromLatLng(place);
                if (position) {
                    appleMapsParams.push(`ll=${position.lat},${position.lng}`);
                }
            }
            const appleMapsUrl = `https://maps.apple.com/?${appleMapsParams.join('&')}`;

            // if we're on iOS, ask the user for Apple Maps or Google
            const choice = await showModalAsync(
                sheets,
                "Open With...",
                [
                    { title: "Google Maps", url: googleMapsUrl },
                    { title: "Apple Maps", url: appleMapsUrl },
                ]);
            if (choice.type === 'choice') {
                window.open(choice.choice.url);
            }
        } else {
            // else use Google
            window.open(googleMapsUrl);
        }
    } else {
        window.open(googleMapsUrl);
    }
}

function PositionMarker({ isMapVisible }: { isMapVisible: boolean }) {
    const { currentPosition, getPosition } = useGeolocationData(isMapVisible); // TODO clear the position if it's stale

    // Ask for location as soon as a map is on screen, instead of waiting for a tap on the locate
    // button. useGeolocationData only resumes tracking *passively* — it starts the watcher when
    // permission is already granted, and otherwise stays dormant. So on a device that had never
    // granted (or whose "Allow Once" grant lapsed) the blue dot simply never appeared, with no
    // prompt and nothing to indicate why. Showing a map is a clear enough signal of intent to
    // justify the prompt. Guarded by a ref because getPosition's identity changes between renders.
    const hasRequestedPosition = useRef(false);
    useEffect(() => {
        if (!isMapVisible || hasRequestedPosition.current) {
            return;
        }
        hasRequestedPosition.current = true;
        getPosition().catch(err => logger.warn(`Could not establish position for the map: ${err}`));
    }, [ isMapVisible, getPosition ]);

    // TODO it'd be good to add an accuracy circle here, perhaps via somehow setting the icon
    //   to a well-known block of SVG and searching the DOM for it, and then adding an accuracy
    //   circle around it. Also, it'd be nice if the circle stroke throbbed, again perhaps via css
    if (isPositionValid(currentPosition)) {
        return <Marker
            position={{ lat: currentPosition.coords.latitude, lng: currentPosition.coords.longitude }}
            icon={{
                path: (window as any).google?.maps?.SymbolPath?.CIRCLE,
                fillColor: 'blue',
                fillOpacity: 1,
                strokeColor: 'blue',
                strokeWeight: 6,
                strokeOpacity: 0.3,
                scale: 5,
            }}
        />;
    } else {
        return null;
    }
}
